import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES } from "../data/site";

const ScrollTypewriter = ({ a, b, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [text, setText] = useState("");
  useEffect(() => {
    if (!inView) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setText(a); return; }
    const phrases = [a, b].filter(Boolean);
    if (phrases.length === 0) return;
    let cancelled = false;
    let timer;
    const wait = (ms) => new Promise((r) => { timer = setTimeout(r, ms); });
    const run = async () => {
      let vi = 0;
      for (let i = 0; i <= phrases[0].length; i++) {
        if (cancelled) return;
        setText(phrases[0].slice(0, i));
        await wait(45);
      }
      while (!cancelled) {
        await wait(2200);
        const cur = phrases[vi];
        for (let i = cur.length; i >= 0; i--) {
          if (cancelled) return;
          setText(cur.slice(0, i));
          await wait(28);
        }
        vi = (vi + 1) % phrases.length;
        const nxt = phrases[vi];
        for (let i = 0; i <= nxt.length; i++) {
          if (cancelled) return;
          setText(nxt.slice(0, i));
          await wait(55);
        }
      }
    };
    run();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [inView, a, b]);
  return (
    <h2 ref={ref} className={className} data-testid="included-title">
      {text}
      <span
        aria-hidden="true"
        className="cr-cursor ml-0.5 inline-block w-[3px] translate-y-1 bg-marigold align-middle"
        style={{ height: "0.9em" }}
      />
    </h2>
  );
};

export const Included = () => {
  const { t } = useLang();

  return (
    <section
      id="included"
      className="relative overflow-hidden bg-purpleink-deep py-24 grain sm:py-32"
      data-testid="included-section"
    >
      {/* decorative sun */}
      <div className="pointer-events-none absolute -right-40 -top-40 h-[30rem] w-[30rem] rounded-full bg-marigold/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="text-xs font-medium uppercase tracking-[0.3em] text-marigold">
              {t.included.kicker}
            </span>
            <ScrollTypewriter
              a={t.included.title}
              b={t.included.title2}
              className="mt-4 min-h-[2.6em] font-display text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl"
            />
            <motion.div
              initial={{ opacity: 0, x: -70, rotate: -2 }}
              whileInView={{ opacity: 1, x: 0, rotate: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 overflow-hidden rounded-[2rem] rounded-bl-none shadow-2xl"
            >
              <img
                src={IMAGES.sjdsSignDay}
                alt="The San Juan del Sur sign and marlin statue by the bay, Nicaragua"
                title="Sunset at the San Juan del Sur bay, Nicaragua"
                loading="lazy"
                className="h-72 w-full object-cover"
              />
            </motion.div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="flex flex-col">
              {t.included.items.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-start gap-4 border-b border-white/12 py-5"
                  data-testid={`included-item-${i}`}
                >
                  <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-teal">
                    <Check className="h-4 w-4 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-lg text-white/90">{item}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
